import { ApplicationRef, Inject, Injectable, Injector, StaticProvider } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { LOCALE_ID } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private readonly STORAGE_KEY = 'user_language';
  private localeSubject = new BehaviorSubject<string>('en-US');

  constructor(
    private translate: TranslateService,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  /**
   * Initialize the language on app start
   */
  initializeLanguage(): void {
    const storedLang = localStorage.getItem(this.STORAGE_KEY) || 'en';
    this.changeLanguage(storedLang, false); // Set language without saving again
  }

  /**
   * Change language, persist selection, and notify Angular
   */
  changeLanguage(lang: string, saveToStorage = true): void {
    if (saveToStorage) {
      localStorage.setItem(this.STORAGE_KEY, lang);
    }

    this.translate.use(lang);

    // Get the corresponding LOCALE_ID
    const localeId = this.getLocaleFromLanguage(lang);
    this.localeSubject.next(localeId);

    // Refresh Angular manually to apply LOCALE_ID
    this.refreshLocaleProvider(localeId);

    console.log(`Language changed to ${lang}, LOCALE_ID updated to ${localeId}`);
  }

  /**
   * Convert language code to LOCALE_ID format
   */
  getLocaleFromLanguage(lang: string): string {
    switch (lang.split('-')[0]) {
      case 'de':
        return 'de-DE';
      case 'es':
        return 'es-ES';
      case 'fr':
        return 'fr-FR';
      case 'it':
        return 'it-IT';
      case 'nl':
        return 'nl-NL';
      case 'hu':
        return 'hu-HU';
      case 'sv':
        return 'sv-SE';
      case 'cs':
        return 'cs-CZ';
      case 'sk':
        return 'sk-SK';
      case 'hr':
        return 'hr-HR';
      case 'sl':
        return 'sl-SI';
      case 'bs':
        return 'bs-BA';
      case 'fi':
        return 'fi-FI';
      case 'ru':
        return 'ru-RU';
      case 'ja':
        return 'ja-JP';
      case 'tr':
        return 'tr-TR';
      case 'zh':
        return 'zh-CN';
      case 'ro':
        return 'ro-RO';
      case 'no':
        return 'nb-NO';
      case 'he':
        return 'he-IL';
      case 'ar':
        return 'ar-SA';
      case 'ko':
        return 'ko-KR';
      case 'pl':
        return 'pl-PL';
      case 'bg':
        return 'bg-BG';
      default:
        return 'en-US';
    }
  }

  /**
   * Check if language is RTL (Right-to-Left)
   */
  isRTL(lang: string): boolean {
    const rtlLanguages = ['he', 'ar', 'fa', 'ur'];
    return rtlLanguages.includes(lang.split('-')[0]);
  }

  /**
   * Get text direction for language
   */
  getTextDirection(lang: string): 'ltr' | 'rtl' {
    return this.isRTL(lang) ? 'rtl' : 'ltr';
  }

  /**
   * ✅ Refresh LOCALE_ID Without Page Reload
   */
  private refreshLocaleProvider(newLocale: string): void {
    const newInjector = Injector.create({
      providers: [{ provide: LOCALE_ID, useValue: newLocale } as StaticProvider],
      parent: this.injector,
    });

    console.log(`LOCALE_ID dynamically updated to: ${newLocale}`);

    // ✅ Trigger Change Detection to apply the change
    this.appRef.tick();
  }

  /**
   * Expose the current locale as an observable
   */
  get locale$() {
    return this.localeSubject.asObservable();
  }
}
